import type { ComponentType } from 'react';
import { hydrateRoot } from 'react-dom/client';
import Modal from 'react-modal';
import initI18n from '@wix/da-i18next/pkg/presetI18nClientConfigNg';
import { getClientInstance } from '@wix/da-ddt';
import { getEnv } from '@wix/da-react-app/pkg/client/Environment/redux/selectors';
import { getRootErrorCallbacks } from '@wix/da-react-app/pkg/client/rootErrorCallbacks';
import { configureHistoryListener } from '@wix/da-shared-react/pkg/redux/routing/history';
import { configureStore } from '../store/configure-store';
import AppDocumentWrapper from '@wix/da-react-app/pkg/client/AppDocumentWrapper';
import { I18nextProvider } from 'react-i18next';
import i18n from 'i18next';

interface ClientSetupOptions {
  component: ComponentType<{ language: string }>;
  enableHistoryListener?: boolean;
}

export function setupClient(options: ClientSetupOptions) {
  const { component: Component, enableHistoryListener = false } = options;

  // Initialize DDT
  const ddt = getClientInstance();
  ddt.replayServerSideLogs(window['__DDT__']);

  // Get initial state
  const initialState = window['__INITIAL_STATE__'];
  delete window['__INITIAL_STATE__'];

  // Configure store
  const store = configureStore(initialState, enableHistoryListener);
  const state = store.getState();

  // Configure history listener if needed
  if (enableHistoryListener) {
    configureHistoryListener(store);
  }

  // Initialize i18n
  const initialI18n = window['__INITIAL_I18N__'];
  initI18n(
    {
      ...initialI18n,
      options: {
        fallbackNS: 'translation',
      },
    },
    getEnv(state).isDebug,
    getEnv(state).flags
  );

  // Configure modal
  Modal.setAppElement('body');

  // Hydrate root
  hydrateRoot(
    document,
    <I18nextProvider i18n={i18n}>
      <AppDocumentWrapper store={store}>
        <Component language={initialI18n.locale} />
      </AppDocumentWrapper>
    </I18nextProvider>,
    getRootErrorCallbacks('editor-standalone')
  );
}
